import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Table, Input, Select, Space, Card, Tooltip } from 'antd';
import { useSearchParams, Link } from 'react-router-dom';
import { formatDateTime } from '../../utils/format';
import { getIncomes } from '../../services/incomes';
import './style.css';

const { Search } = Input;
const { Option } = Select;

const stateMap = {
  0: { cn: '待处理', en: 'Pending' },
  1: { cn: '确认中', en: 'Confirming' },
  2: { cn: '已确认', en: 'Confirmed' },
  3: { cn: '准备归集', en: 'Ready for Sweep' },
  4: { cn: '已创建归集', en: 'Sweep Created' },
  5: { cn: '已归集', en: 'Swept' },
  6: { cn: '待归集', en: 'Pending Sweep' },
  7: { cn: '尘额', en: 'Dust' },
  8: { cn: '不支持', en: 'Unsupported' },
  9: { cn: '已冻结', en: 'Frozen' },
  10: { cn: '失败', en: 'Failed' },
  11: { cn: '未审核', en: 'Unreviewed' },
  12: { cn: 'AML未检查', en: 'AML Unchecked' },
  13: { cn: '黑名单', en: 'Blacklisted' },
  14: { cn: '已打包', en: 'Packed' }
};

const IncomeList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const filtersFromUrl = useMemo(() => {
    const f = {};
    for (const [key, value] of searchParams.entries()) {
      f[key] = value;
    }
    return f;
  }, [searchParams]);

  const truncateText = (text) => {
    if (!text || text.length <= 10) return text;
    return `${text.substr(0, 5)}...${text.substr(-5)}`;
  };

  const renderWithTooltip = (text) => (
    <Tooltip placement="topLeft" title={text} copyable>
      {truncateText(text)}
    </Tooltip>
  );

  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await getIncomes({
        page: params.current || 1,
        page_size: params.pageSize || 20,
        ...filtersFromUrl,
      });
      setData(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.meta.total,
        current: response.meta.page,
        pageSize: response.meta.page_size,
      }));
    } catch (error) {
      console.error('获取收入记录失败:', error);
    }
    setLoading(false);
  }, [filtersFromUrl]);

  const { current, pageSize } = pagination;
  useEffect(() => {
    fetchData({
      current,
      pageSize,
    });
  }, [fetchData, current, pageSize]);

  const handleTableChange = (newPagination) => {
    fetchData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = (field, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(field, value);
      } else {
        next.delete(field);
      }
      return next;
    }, { replace: true });
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '链',
      dataIndex: 'chain',
      key: 'chain',
      width: 100,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount, record) => (
        <>
          {parseFloat(amount)}{' '}
          {record.asset_id ? (
            <Link to={`/dashboard/explorer/asset/${record.asset_id}`}>{record.symbol}</Link>
          ) : record.symbol}
        </>
      ),
      align: 'right',
    },
    {
      title: '状态',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => stateMap[state]?.cn || state,
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: 140,
      ellipsis: false,
      render: (text, record) => text ? (
        <Tooltip placement="topLeft" title={text} copyable>
          <Link to={`/dashboard/explorer/address/${record.income_address_id}`} style={{ fontFamily: 'monospace' }}>
            {truncateText(text)}
          </Link>
        </Tooltip>
      ) : '-',
    },
    {
      title: '交易ID',
      dataIndex: 'txid',
      key: 'txid',
      width: 140,
      ellipsis: false,
      render: (text) => text ? (
        <Tooltip placement="topLeft" title={text} copyable>
          <Link to={`/dashboard/explorer/transaction/${text}`} style={{ fontFamily: 'monospace' }}>
            {truncateText(text)}
          </Link>
        </Tooltip>
      ) : '-',
    },
    {
      title: '备注',
      dataIndex: 'memo',
      key: 'memo',
      width: 140,
      ellipsis: false,
      render: renderWithTooltip,
    },
    {
      title: '创建时间',
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      width: 180,
      render: (d) => formatDateTime(d),
    },
  ];

  return (
    <Card title="收入记录">
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="搜索地址"
          defaultValue={filtersFromUrl.address || ''}
          onSearch={(value) => handleSearch('address', value)}
          style={{ width: 200 }}
        />
        <Search
          placeholder="搜索交易ID"
          defaultValue={filtersFromUrl.txid || ''}
          onSearch={(value) => handleSearch('txid', value)}
          style={{ width: 200 }}
        />
        <Search
          placeholder="搜索链名称"
          defaultValue={filtersFromUrl.chain || ''}
          onSearch={(value) => handleSearch('chain', value)}
          style={{ width: 200 }}
        />
        <Search
          placeholder="搜索资产名称"
          defaultValue={filtersFromUrl.symbol || ''}
          onSearch={(value) => handleSearch('symbol', value)}
          style={{ width: 200 }}
        />
        <Select
          mode="multiple"
          placeholder="选择状态"
          allowClear
          style={{ minWidth: 240 }}
          value={filtersFromUrl.state?.split(',').filter(Boolean) || []}
          onChange={(value) => handleSearch('state', value.join(','))}
        >
          {Object.entries(stateMap).map(([key, { cn, en }]) => (
            <Option key={key} value={key}>{cn} ({en})</Option>
          ))}
        </Select>
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        scroll={{ x: 700 }}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default IncomeList;
